import {
    getAddress,
    isAddressEqual,
    zeroAddress,
    type Address,
    type Hex,
} from 'viem';
import {
    readContract,
    signTypedData,
    waitForTransactionReceipt,
    writeContract,
} from 'wagmi/actions';

import { ensureTypedDataChain, walletWagmiConfig } from '@/features/wallet';
import {
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import {
    provisionAuthorizationWallet,
    requestAccountHashAssociation,
    requestAuthorizationRegistryChallenge,
} from '@/lib/api/authRegistry';
import { custodialManagerFor } from '@/lib/config/custodialManagers';

import {
    completeOAuthAccountBinding,
    OAuthAccountHashInUseError,
    type AccountHashBinding,
    type OAuthAccountBindingResult,
    type OAuthBindingRoute,
} from './accountBinding';

export {
    isOAuthAccountHashInUseError,
    isOAuthBindingRouteMismatchError,
    OAuthAccountHashInUseError,
    OAuthBindingRouteMismatchError,
} from './accountBinding';
export type { AccountHashBinding, OAuthBindingRoute } from './accountBinding';

function accountHashHex(value: string): Hex {
    if (!/^0x[0-9a-fA-F]{64}$/u.test(value)) {
        throw new Error('Expected a 0x-prefixed 32-byte account hash.');
    }
    return value.toLowerCase() as Hex;
}

const managerAbi = [
    {
        name: 'wallets',
        type: 'function',
        stateMutability: 'view',
        inputs: [{ name: 'owner', type: 'address' }],
        outputs: [{ type: 'address' }],
    },
    {
        name: 'getWalletByAccountHash',
        type: 'function',
        stateMutability: 'view',
        inputs: [{ name: '_accountHash', type: 'bytes32' }],
        outputs: [
            { name: 'ownerAddress', type: 'address' },
            { name: 'walletAddress', type: 'address' },
        ],
    },
] as const;

const custodialWalletAbi = [
    {
        name: 'addAccountHash',
        type: 'function',
        stateMutability: 'nonpayable',
        inputs: [
            { name: '_hashes', type: 'bytes32[]' },
            { name: '_signer', type: 'address' },
            { name: '_issued', type: 'uint256' },
            { name: '_expiry', type: 'uint256' },
            { name: '_digest', type: 'bytes32' },
            { name: '_sig', type: 'bytes' },
        ],
        outputs: [],
    },
    {
        name: 'removeAccountHash',
        type: 'function',
        stateMutability: 'nonpayable',
        inputs: [{ name: '_hashes', type: 'bytes32[]' }],
        outputs: [],
    },
    {
        name: 'accountHashes',
        type: 'function',
        stateMutability: 'view',
        inputs: [{ name: '', type: 'bytes32' }],
        outputs: [{ type: 'bool' }],
    },
] as const;

export class OAuthAccountRemovalPendingError extends Error {
    readonly code = 'oauthAccountRemovalPending';

    constructor(
        readonly transactionHash: Hex,
        cause: unknown,
    ) {
        super(
            cause instanceof Error
                ? cause.message
                : 'The removal transaction was submitted, but its final state could not be confirmed.',
        );
        this.name = 'OAuthAccountRemovalPendingError';
        Object.assign(this, { cause });
    }
}

export function isOAuthAccountRemovalPendingError(
    error: unknown,
): error is OAuthAccountRemovalPendingError {
    return (
        !!error &&
        typeof error === 'object' &&
        (error as { code?: unknown }).code === 'oauthAccountRemovalPending'
    );
}

export class OAuthAccountBindingRecoveryRequiredError extends Error {
    readonly code = 'oauthAccountBindingRecoveryRequired';

    constructor(
        readonly registeredOwner: Address,
        readonly authorizationWallet: Address,
    ) {
        super(
            'The login hash was removed from the custodial wallet, but the manager registry could not finish cleanup. Trading is blocked; contact support and do not retry the unlink transaction.',
        );
        this.name = 'OAuthAccountBindingRecoveryRequiredError';
    }
}

export function isOAuthAccountBindingRecoveryRequiredError(
    error: unknown,
): error is OAuthAccountBindingRecoveryRequiredError {
    return (
        !!error &&
        typeof error === 'object' &&
        (error as { code?: unknown }).code ===
            'oauthAccountBindingRecoveryRequired'
    );
}

/**
 * Which owner/wallet the anchor's manager has this login hash registered to.
 * `null` means it is free to claim.
 */
export async function getAccountHashBinding(
    accountHash: string,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Promise<AccountHashBinding | null> {
    const hash = accountHashHex(accountHash);
    const [registeredOwner, registeredWallet] = await readContract(
        walletWagmiConfig,
        {
            address: custodialManagerFor(anchorChainId),
            abi: managerAbi,
            functionName: 'getWalletByAccountHash',
            args: [hash],
            chainId: anchorChainId,
        },
    );
    if (isAddressEqual(registeredWallet, zeroAddress)) return null;
    return {
        owner: getAddress(registeredOwner),
        wallet: getAddress(registeredWallet),
    };
}

/**
 * `wallets(owner)` on the anchor's manager: the authorization wallet this owner
 * already has there, or `null` when it owns none. The route a login can take is
 * decided by this one read, so surfaces preflight it before offering a choice
 * the chain would refuse.
 */
export async function getOwnedAuthorizationWallet(
    ownerInput: string,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Promise<Address | null> {
    const wallet = await readContract(walletWagmiConfig, {
        address: custodialManagerFor(anchorChainId),
        abi: managerAbi,
        functionName: 'wallets',
        args: [getAddress(ownerInput)],
        chainId: anchorChainId,
    });
    const normalized = getAddress(wallet);
    return isAddressEqual(normalized, zeroAddress) ? null : normalized;
}

/**
 * The manager reverts with `M:HASH_IN_USE` when a live hash is re-registered.
 * A preflight normally catches it, but a race (or a hash registered between the
 * read and the write) still has to read as the same dead end rather than as raw
 * calldata.
 */
async function mapHashInUseError(
    error: unknown,
    accountHash: string,
    anchorChainId: AnchorChainId,
): Promise<never> {
    if (!/HASH_IN_USE/iu.test(errorText(error))) throw error;
    const registered = await getAccountHashBinding(
        accountHash,
        anchorChainId,
    ).catch(() => null);
    throw new OAuthAccountHashInUseError(
        registered?.owner ?? zeroAddress,
        registered?.wallet ?? zeroAddress,
    );
}

function errorText(error: unknown): string {
    if (!error || typeof error !== 'object') return String(error ?? '');
    const body = (error as { body?: { error?: unknown; code?: unknown } }).body;
    return [
        (error as { message?: unknown }).message,
        (error as { code?: unknown }).code,
        body?.error,
        body?.code,
    ]
        .filter((value) => typeof value === 'string')
        .join(' ');
}

export async function completeOAuthAccountBindingWithWallet(
    ownerInput: string,
    accountHash: string,
    expectedRoute?: OAuthBindingRoute,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Promise<OAuthAccountBindingResult> {
    const owner = getAddress(ownerInput);
    const hash = accountHashHex(accountHash);

    return completeOAuthAccountBinding(
        { owner, accountHash: hash, expectedRoute, anchorChainId },
        {
            getAccountHashBinding: async (identityHash) =>
                getAccountHashBinding(identityHash, anchorChainId),
            getAuthorizationWallet: async (walletOwner) =>
                getOwnedAuthorizationWallet(walletOwner, anchorChainId),
            requestAssociation: async (authorizationWallet, identityHash) =>
                requestAccountHashAssociation(
                    authorizationWallet,
                    identityHash,
                    anchorChainId,
                ),
            addAccountHash: async (
                authorizationWallet,
                identityHash,
                association,
            ) =>
                writeContract(walletWagmiConfig, {
                    account: owner,
                    address: authorizationWallet,
                    abi: custodialWalletAbi,
                    functionName: 'addAccountHash',
                    args: [
                        [identityHash],
                        getAddress(association.signer),
                        BigInt(association.issued),
                        BigInt(association.expiry),
                        association.digest as Hex,
                        association.signature as Hex,
                    ],
                    chainId: anchorChainId,
                }).catch(async (caught) =>
                    mapHashInUseError(caught, hash, anchorChainId),
                ),
            waitForReceipt: async (transactionHash) => {
                await waitForTransactionReceipt(walletWagmiConfig, {
                    hash: transactionHash,
                    chainId: anchorChainId,
                });
            },
            requestProvisionChallenge: async (challengeOwner, identityHash) =>
                requestAuthorizationRegistryChallenge(
                    challengeOwner,
                    identityHash,
                    anchorChainId,
                ),
            signOwnerBinding: async (typedData) => {
                await ensureTypedDataChain(typedData);
                return signTypedData(walletWagmiConfig, {
                    account: owner,
                    ...typedData,
                } as Parameters<typeof signTypedData>[1]);
            },
            provisionAuthorizationWallet: async (input) =>
                provisionAuthorizationWallet({
                    ...input,
                    anchorChainId,
                }).catch(async (caught) =>
                    mapHashInUseError(caught, hash, anchorChainId),
                ),
        },
    );
}

export async function isOAuthAccountHashBoundToOwner(
    ownerInput: string,
    accountHash: string,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Promise<boolean> {
    const owner = getAddress(ownerInput);
    const hash = accountHashHex(accountHash);
    const manager = custodialManagerFor(anchorChainId);
    const [managerLookup, currentAuthorizationWallet] = await Promise.all([
        readContract(walletWagmiConfig, {
            address: manager,
            abi: managerAbi,
            functionName: 'getWalletByAccountHash',
            args: [hash],
            chainId: anchorChainId,
        }),
        readContract(walletWagmiConfig, {
            address: manager,
            abi: managerAbi,
            functionName: 'wallets',
            args: [owner],
            chainId: anchorChainId,
        }),
    ]);
    const [registeredOwner, registeredWallet] = managerLookup;
    return (
        !isAddressEqual(currentAuthorizationWallet, zeroAddress) &&
        isAddressEqual(registeredOwner, owner) &&
        isAddressEqual(registeredWallet, currentAuthorizationWallet)
    );
}

export async function verifyOAuthAccountBindingRemoved(
    authorizationWalletInput: string,
    accountHash: string,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Promise<boolean> {
    const authorizationWallet = getAddress(authorizationWalletInput);
    const hash = accountHashHex(accountHash);
    const [isLocallyActive, managerLookup] = await Promise.all([
        readContract(walletWagmiConfig, {
            address: authorizationWallet,
            abi: custodialWalletAbi,
            functionName: 'accountHashes',
            args: [hash],
            chainId: anchorChainId,
        }),
        readContract(walletWagmiConfig, {
            address: custodialManagerFor(anchorChainId),
            abi: managerAbi,
            functionName: 'getWalletByAccountHash',
            args: [hash],
            chainId: anchorChainId,
        }),
    ]);
    const [registeredOwner, registeredWallet] = managerLookup;
    const managerBindingCleared =
        isAddressEqual(registeredOwner, zeroAddress) &&
        isAddressEqual(registeredWallet, zeroAddress);

    if (!isLocallyActive && managerBindingCleared) return true;
    if (
        isLocallyActive &&
        !managerBindingCleared &&
        isAddressEqual(registeredWallet, authorizationWallet)
    ) {
        return false;
    }
    if (
        !isLocallyActive &&
        !managerBindingCleared &&
        isAddressEqual(registeredWallet, authorizationWallet)
    ) {
        throw new OAuthAccountBindingRecoveryRequiredError(
            getAddress(registeredOwner),
            authorizationWallet,
        );
    }
    throw new Error(
        'The local and manager OAuth account-hash registries disagree.',
    );
}

export async function removeOAuthAccountBindingWithWallet(
    ownerInput: string,
    authorizationWalletInput: string,
    accountHash: string,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Promise<Hex | null> {
    const owner = getAddress(ownerInput);
    const authorizationWallet = getAddress(authorizationWalletInput);
    const hash = accountHashHex(accountHash);
    const manager = custodialManagerFor(anchorChainId);

    const [managerLookup, currentAuthorizationWallet, isLocallyActive] =
        await Promise.all([
            readContract(walletWagmiConfig, {
                address: manager,
                abi: managerAbi,
                functionName: 'getWalletByAccountHash',
                args: [hash],
                chainId: anchorChainId,
            }),
            readContract(walletWagmiConfig, {
                address: manager,
                abi: managerAbi,
                functionName: 'wallets',
                args: [owner],
                chainId: anchorChainId,
            }),
            readContract(walletWagmiConfig, {
                address: authorizationWallet,
                abi: custodialWalletAbi,
                functionName: 'accountHashes',
                args: [hash],
                chainId: anchorChainId,
            }),
        ]);
    const [registeredOwner, registeredWallet] = managerLookup;
    const managerBindingCleared =
        isAddressEqual(registeredOwner, zeroAddress) &&
        isAddressEqual(registeredWallet, zeroAddress);
    if (managerBindingCleared) {
        if (isLocallyActive) {
            throw new Error(
                'The manager binding is cleared, but the local authorization hash is still active.',
            );
        }
        return null;
    }
    if (
        !isAddressEqual(registeredOwner, owner) ||
        !isAddressEqual(registeredWallet, authorizationWallet)
    ) {
        throw new Error(
            'The connected wallet does not own the authorization wallet linked to this OAuth account.',
        );
    }
    if (!isLocallyActive) {
        throw new OAuthAccountBindingRecoveryRequiredError(
            getAddress(registeredOwner),
            authorizationWallet,
        );
    }
    if (!isAddressEqual(currentAuthorizationWallet, authorizationWallet)) {
        throw new Error(
            "The linked authorization wallet is no longer the owner's current wallet.",
        );
    }

    const transactionHash = await writeContract(walletWagmiConfig, {
        account: owner,
        address: authorizationWallet,
        abi: custodialWalletAbi,
        functionName: 'removeAccountHash',
        args: [[hash]],
        chainId: anchorChainId,
    });

    try {
        const receipt = await waitForTransactionReceipt(walletWagmiConfig, {
            hash: transactionHash,
            chainId: anchorChainId,
        });
        if (receipt.status !== 'success') {
            throw new Error('The OAuth account removal transaction reverted.');
        }
        if (
            !(await verifyOAuthAccountBindingRemoved(
                authorizationWallet,
                accountHash,
                anchorChainId,
            ))
        ) {
            throw new Error(
                'The OAuth account hash is still registered after the removal transaction.',
            );
        }
    } catch (caught) {
        throw new OAuthAccountRemovalPendingError(transactionHash, caught);
    }

    return transactionHash;
}
